// Tracks the observable state of each data type's load (queued/loading/done/error
// plus percentage), independent of ApiLoadScheduler (which only handles admission
// and concurrency). Built to plug straight into React's useSyncExternalStore: every
// update replaces the entry with a new object, so snapshots are safe to compare by
// reference, and reads never allocate a new object when nothing changed.
//
// One store now covers every kind of load (regular/blocked/archived/live) - each
// entry is looked up by a composite `key` (see DandDAPIClass.loadKey), but still
// carries its own data_type/blocked/archive_id/live so consumers can label and
// filter entries without having to parse the key back apart.

import { LoadPriority } from "./api_load_scheduler";

export type LoadState = "queued" | "preloading" | "loading" | "done" | "error" | "waiting" | "processing";

export interface DataTypeLoadStatus {
  data_type: string;
  blocked: boolean;
  archive_id: string;
  live: boolean;
  user_scope: boolean; // The second-phase load for the user's non-free source books (see DandDAPIClass.loadDataType).
  priority: LoadPriority;
  state: LoadState;
  percentage: number; // 0-1
  completed_at?: number; // Date.now() when it reached "done" - lets a page ignore loads that finished before it opened.
}

type Listener = () => void;

export class ApiLoadStatusStore {
  private statuses = new Map<string, DataTypeLoadStatus>();
  private listeners = new Set<Listener>();
  private all_snapshot: DataTypeLoadStatus[] = [];

  private notify() {
    this.all_snapshot = Array.from(this.statuses.values());
    this.listeners.forEach(listener => listener());
  }

  private update(key: string, patch: Partial<DataTypeLoadStatus>) {
    const existing = this.statuses.get(key);
    const next: DataTypeLoadStatus = {
      data_type: existing?.data_type ?? patch.data_type ?? key,
      blocked: existing?.blocked ?? patch.blocked ?? false,
      archive_id: existing?.archive_id ?? patch.archive_id ?? "",
      live: existing?.live ?? patch.live ?? false,
      user_scope: existing?.user_scope ?? patch.user_scope ?? false,
      priority: existing?.priority ?? "standard",
      state: existing?.state ?? "queued",
      percentage: existing?.percentage ?? 0,
      ...patch,
    };
    this.statuses.set(key, next);
    this.notify();
  }

  // Called when a data type is first requested and hasn't started loading yet.
  setQueued(key: string, data_type: string, blocked: boolean, archive_id: string, live: boolean, priority: LoadPriority, user_scope = false) {
    if (this.statuses.get(key)?.state === "loading") {
      return; // already past "queued" - don't regress it
    }
    this.update(key, { data_type, blocked, archive_id, live, user_scope, priority, state: "queued", percentage: 0, completed_at: undefined });
  }

  // Called when a queued/in-flight entry gets bumped to a different priority.
  promote(key: string, priority: LoadPriority) {
    if (this.statuses.has(key)) {
      this.update(key, { priority });
    }
  }

  setLoading(key: string, preloading: boolean = false) {
    this.update(key, { state: preloading ? "preloading" : "loading" });
  }

  setProgress(key: string, percentage: number, preloading: boolean = false) {
    this.update(key, { state: preloading ? "preloading" : "loading", percentage });
  }

  setDone(key: string) {
    this.update(key, { state: "done", percentage: 1, completed_at: Date.now() });
  }

  setError(key: string) {
    this.update(key, { state: "error" });
  }

  setWaitingForDependencies(key: string) {
    this.update(key, { state: "waiting" });
  }

  setProcessing(key: string) {
    this.update(key, { state: "processing" });
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Stable reference until the next notify() - safe for useSyncExternalStore.
  getSnapshot = (): DataTypeLoadStatus[] => this.all_snapshot;

  getOne = (key: string): DataTypeLoadStatus | undefined => this.statuses.get(key);

  // For reloads (spoof/unspoof, logout, switching users) - otherwise a data type
  // stays "done" after its hash has been emptied, and nothing reloads it.
  clear() {
    this.statuses.clear();
    this.notify();
  }
}
